<template>
  <section class="admin-user-dashboard" aria-label="User management overview">
    <header class="admin-dashboard-header">
      <div>
        <p class="admin-kicker">User management</p>
        <h2>Group Administration</h2>
      </div>
      <button type="button" class="admin-command" :disabled="refreshing" @click="refresh">
        <span class="material-symbols-outlined" aria-hidden="true">refresh</span>
        Refresh
      </button>
    </header>

    <div class="admin-stat-grid">
      <article v-for="stat in stats" :key="stat.label" class="admin-stat">
        <span class="material-symbols-outlined" aria-hidden="true">{{ stat.icon }}</span>
        <div>
          <p>{{ stat.label }}</p>
          <strong>{{ stat.value }}</strong>
        </div>
      </article>
    </div>

    <div v-if="message" class="admin-message" :class="{ error: messageType === 'error' }">
      {{ message }}
    </div>

    <div class="admin-table-wrap">
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Role</th>
            <th>Status</th>
            <th>Access</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="users.length === 0">
            <td colspan="5" class="empty-cell">No users found for this lab.</td>
          </tr>
          <tr v-for="user in users" :key="canonicalId(user)">
            <td>
              <div class="user-cell">
                <span class="mini-avatar" aria-hidden="true">{{ initialsFor(user) }}</span>
                <strong>{{ user.name || "Unnamed user" }}</strong>
              </div>
            </td>
            <td>{{ user.email }}</td>
            <td>
              <select
                :value="user.role"
                :disabled="user.isLabSuper || savingUserId === canonicalId(user)"
                @change="updateRole(user, $event.target.value)"
              >
                <option value="temp">Pending</option>
                <option value="user">Lab Member</option>
                <option value="lab-admin">Lab Admin</option>
              </select>
            </td>
            <td>
              <span class="status-pill" :class="{ online: isOnline(user), pending: user.role === 'temp' }">
                {{ user.role === "temp" ? "Pending" : isOnline(user) ? "Online" : "Offline" }}
              </span>
            </td>
            <td>
              <span class="access-text">{{ accessLabel(user) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
export default {
  name: "AdminUserDashboard",
  emits: ["userChanged"],
  data() {
    return {
      refreshing: false,
      savingUserId: "",
      message: "",
      messageType: "success",
    };
  },
  computed: {
    users() {
      return (this.appStore.state.users || []).filter((user) => !this.isSystemUser(user));
    },
    activeLabId() {
      return this.canonicalId(this.appStore.state.activeUser?.activeLabId) || this.canonicalId(this.appStore.state.activeUser?.labId);
    },
    stats() {
      return [
        { label: "Total Users", value: this.users.length, icon: "groups" },
        { label: "Pending Approval", value: this.users.filter((user) => user.role === "temp").length, icon: "pending_actions" },
        { label: "Admins", value: this.users.filter((user) => user.role === "lab-admin").length, icon: "admin_panel_settings" },
        { label: "Online", value: this.users.filter((user) => this.isOnline(user)).length, icon: "podcasts" },
      ];
    },
  },
  methods: {
    canonicalId(value) {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value.id || "";
    },
    isSystemUser(user) {
      return (
        typeof user?.email === "string" &&
        user.email.endsWith("@subangstrom.local") &&
        user.email.startsWith("system+")
      );
    },
    isOnline(user) {
      const id = this.canonicalId(user);
      return this.canonicalId(this.appStore.state.activeUser) === id || this.appStore.state.usersOnline.has(id);
    },
    initialsFor(user) {
      return (
        String(user?.name || user?.email || "?")
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase())
          .join("") || "?"
      );
    },
    accessLabel(user) {
      if (user.role === "lab-admin") return "Full lab admin";
      return user.globalDewarAccess || "Custom/project grants";
    },
    async refresh() {
      if (!this.activeLabId) return;
      this.refreshing = true;
      this.message = "";
      try {
        await this.appStore.dispatch("getAdminStuff", {
          url: "users",
          labid: this.activeLabId,
        });
        this.$emit("userChanged");
      } catch (error) {
        this.messageType = "error";
        this.message = error.message || "Unable to refresh users.";
      } finally {
        this.refreshing = false;
      }
    },
    async updateRole(user, role) {
      const userId = this.canonicalId(user);
      if (!userId || role === user.role) return;
      this.savingUserId = userId;
      this.message = "";
      try {
        const body = { role };
        if (role === "user" && user.role === "temp") {
          body.globalDewarAccess = user.globalDewarAccess || "readonly";
        }
        await this.appStore.dispatch("updateItem", {
          item: "users",
          objid: userId,
          labid: this.activeLabId,
          body,
        });
        this.messageType = "success";
        this.message = "User updated.";
        this.$emit("userChanged");
      } catch (error) {
        this.messageType = "error";
        this.message = error.message || "Unable to update user.";
      } finally {
        this.savingUserId = "";
      }
    },
  },
};
</script>

<style scoped>
.admin-user-dashboard {
  display: grid;
  gap: 14px;
  margin: 14px 16px 18px;
  border: 1px solid #d7e2ef;
  border-radius: 8px;
  background: #fff;
  padding: 16px;
  box-shadow: 0 10px 22px rgba(15, 35, 55, 0.07);
}

.admin-dashboard-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
}

.admin-kicker {
  margin: 0 0 4px;
  color: #64748b;
  font-size: 0.72rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.admin-dashboard-header h2 {
  margin: 0;
  color: #0f3654;
  font-size: 1.35rem;
  font-weight: 900;
}

.admin-command {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  width: auto;
  min-height: 36px;
  margin: 0;
  border: 1px solid #0b4f71;
  border-radius: 7px;
  background: #0b4f71;
  padding: 0 13px;
  color: #fff;
  font-weight: 900;
  cursor: pointer;
}

.admin-stat-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
}

.admin-stat {
  display: grid;
  grid-template-columns: 34px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  border: 1px solid #d7e2ef;
  border-radius: 7px;
  background: #f8fbff;
  padding: 12px;
}

.admin-stat .material-symbols-outlined {
  color: #0b4f71;
  font-size: 28px;
}

.admin-stat p {
  margin: 0 0 2px;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
}

.admin-stat strong {
  color: #123a59;
  font-size: 1.25rem;
}

.admin-message {
  border: 1px solid #a7e6bc;
  border-radius: 7px;
  background: #f1fbf5;
  padding: 9px 10px;
  color: #166534;
  font-size: 0.82rem;
  font-weight: 800;
}

.admin-message.error {
  border-color: #fecaca;
  background: #fff1f2;
  color: #b42318;
}

.admin-table-wrap {
  overflow-x: auto;
  border: 1px solid #d7e2ef;
  border-radius: 7px;
}

table {
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;
}

th,
td {
  border-bottom: 1px solid #e5edf6;
  padding: 10px;
  color: #172033;
  font-size: 0.78rem;
  text-align: left;
}

th {
  background: #f8fbff;
  color: #64748b;
  font-size: 0.68rem;
  font-weight: 900;
  text-transform: uppercase;
}

tr:last-child td {
  border-bottom: 0;
}

.user-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: #0b4f71;
  color: #fff;
  font-size: 0.68rem;
  font-weight: 900;
}

select {
  max-width: 150px;
  border: 1px solid #cbd7e6;
  border-radius: 6px;
  background: #fff;
  padding: 5px 7px;
  color: #172033;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 800;
}

.status-pill {
  display: inline-flex;
  border-radius: 999px;
  background: #e5edf6;
  padding: 3px 8px;
  color: #475569;
  font-size: 0.68rem;
  font-weight: 900;
}

.status-pill.online {
  background: #dcfce7;
  color: #166534;
}

.status-pill.pending {
  background: #fef3c7;
  color: #92400e;
}

.access-text {
  color: #475569;
  font-size: 0.74rem;
  font-weight: 800;
}

.empty-cell {
  color: #64748b;
  font-weight: 800;
  text-align: center;
}

@media (max-width: 900px) {
  .admin-dashboard-header {
    align-items: stretch;
    flex-direction: column;
  }

  .admin-command {
    justify-content: center;
  }

  .admin-stat-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
